import type { PlacedAircraft3D } from '@/services/3d/AircraftLoaderService'
import { FLEET_SPECS, HERO_SPEC } from './aircraftCatalog'

/**
 * Baseline fleet — 8 aircraft already in the hangar before hero arrives.
 *
 * Hangar is 220ft (W) × 180ft (D), doors on south face.
 * Scene axes: +X = east, +Z = south (toward doors), +Y = up.
 * Position [0,0,0] = scene center = hangar center at floor level.
 *
 * Layout approach: two rows
 *   Back row (Z ≈ -55):  4 aircraft side-by-side (nose-in from south)
 *   Front row (Z ≈ -10): 4 aircraft, slightly tighter (nose pointing south, easy exit)
 *
 * All rotations in degrees (heading). 180 = nose pointing north (into the back of hangar).
 */
export const baselineFleet: PlacedAircraft3D[] = [
  // ─── Back row (deep, nose-in) ───────────────────────────────────────────
  {
    id: 'alloc-kr1',
    aircraftId: 'ac-kr1',
    tailNumber: 'N350KA',
    typeId: FLEET_SPECS['king-air-350'].type.id,
    position: [-85, 0, -65],
    rotation: 180,
    scale: 1,
    aircraftType: FLEET_SPECS['king-air-350'].type,
    physicalDimensions: FLEET_SPECS['king-air-350'].dims,
  },
  {
    id: 'alloc-cx1',
    aircraftId: 'ac-cx1',
    tailNumber: 'N525XL',
    typeId: FLEET_SPECS['citation-xls'].type.id,
    position: [-28, 0, -62],
    rotation: 180,
    scale: 1,
    aircraftType: FLEET_SPECS['citation-xls'].type,
    physicalDimensions: FLEET_SPECS['citation-xls'].dims,
  },
  {
    id: 'alloc-ch1',
    aircraftId: 'ac-ch1',
    tailNumber: 'N350CS',
    typeId: FLEET_SPECS['challenger-350'].type.id,
    position: [42, 0, -60],
    rotation: 180,
    scale: 1,
    aircraftType: FLEET_SPECS['challenger-350'].type,
    physicalDimensions: FLEET_SPECS['challenger-350'].dims,
  },
  {
    id: 'alloc-g2',
    aircraftId: 'ac-g2',
    tailNumber: 'N280GS',
    typeId: FLEET_SPECS['gulfstream-g280'].type.id,
    position: [90, 0, -55],
    rotation: 180,
    scale: 1,
    aircraftType: FLEET_SPECS['gulfstream-g280'].type,
    physicalDimensions: FLEET_SPECS['gulfstream-g280'].dims,
  },
  // ─── Front row (closer to doors, heads-out for quick exit) ────────────
  {
    id: 'alloc-ph1',
    aircraftId: 'ac-ph1',
    tailNumber: 'N300PE',
    typeId: FLEET_SPECS['phenom-300'].type.id,
    position: [-75, 0, 5],
    rotation: 180,
    scale: 1,
    aircraftType: FLEET_SPECS['phenom-300'].type,
    physicalDimensions: FLEET_SPECS['phenom-300'].dims,
  },
  {
    id: 'alloc-cj1',
    aircraftId: 'ac-cj1',
    tailNumber: 'N525CJ',
    typeId: FLEET_SPECS['citation-cj4'].type.id,
    position: [-18, 0, 8],
    rotation: 180,
    scale: 1,
    aircraftType: FLEET_SPECS['citation-cj4'].type,
    physicalDimensions: FLEET_SPECS['citation-cj4'].dims,
  },
  {
    id: 'alloc-pc1',
    aircraftId: 'ac-pc1',
    tailNumber: 'N12NGX',
    typeId: FLEET_SPECS['pilatus-pc12'].type.id,
    position: [45, 0, 10],
    rotation: 180,
    scale: 1,
    aircraftType: FLEET_SPECS['pilatus-pc12'].type,
    physicalDimensions: FLEET_SPECS['pilatus-pc12'].dims,
  },
  {
    id: 'alloc-hj1',
    aircraftId: 'ac-hj1',
    tailNumber: 'N420HJ',
    typeId: FLEET_SPECS['hondajet'].type.id,
    position: [88, 0, 18],
    rotation: 180,
    scale: 1,
    aircraftType: FLEET_SPECS['hondajet'].type,
    physicalDimensions: FLEET_SPECS['hondajet'].dims,
  },
]

/** Hero aircraft starting position — just outside the hangar doors (south) before animation */
export const heroAircraftEntry: PlacedAircraft3D = {
  id: 'alloc-hero',
  aircraftId: 'ac-hero',
  tailNumber: 'C-GBLR',
  typeId: HERO_SPEC.type.id,
  position: [0, 0, 140],  // outside doors
  rotation: 180,
  scale: 1,
  aircraftType: HERO_SPEC.type,
  physicalDimensions: HERO_SPEC.dims,
}
